import getRouterBasename from '@/lib/router';
import { toast } from 'sonner';

import {
  ChainlitAPI,
  ClientError,
  setAuthClearSuppressed,
  setSuppressAuthExpiry
} from '@chainlit/react-client';

let devServer = 'http://localhost:8000' + getRouterBasename();
if (import.meta.env.VITE_CHAINLIT_URL != null) {
  devServer =
    `http://${import.meta.env.VITE_CHAINLIT_URL}:${
      import.meta.env.VITE_CHAINLIT_PORT
    }` + getRouterBasename();
}
const url = import.meta.env.DEV
  ? devServer
  : window.origin + getRouterBasename();
const serverUrl = new URL(url);

const httpEndpoint = serverUrl.toString();

let accountExpiredModalActive = false;

/**
 * While the account-expired modal owns the screen (#1054), suppress the
 * background-poll 401 redirect so the modal's own 20s auto-logout drives
 * navigation — otherwise a token-usage poll 401s on the just-disabled account
 * and bounces the operator to /login before they can read the notice. Also
 * suppresses `useApi`'s own 401-triggered `setUser(null)` (#1103) — that path
 * bypasses `on401` entirely (`useApi` clones the client with `on401`/`onError`
 * cleared) and clearing `user` immediately trips the app's unauthenticated
 * redirect, tearing the modal down long before its countdown finishes. Also
 * suppresses the react-client `/user` poll's own 401 -> setUser(null)
 * reaction (#1140) — unguarded, that path flips `isAuthenticated` to false
 * and `App.tsx` tears the page down to the logged-out route immediately,
 * regardless of the modal's countdown.
 */
export const setAccountExpiredModalActive = (active: boolean) => {
  accountExpiredModalActive = active;
  setAuthClearSuppressed(active);
  setSuppressAuthExpiry(active);
};

const on401 = () => {
  if (accountExpiredModalActive) {
    return;
  }
  if (window.location.pathname !== getRouterBasename() + '/login') {
    // The credentials aren't correct, remove the token and redirect to login
    window.location.href = getRouterBasename() + '/login';
  }
};

const onError = (error: ClientError) => {
  toast.error(error.toString());
};

export type CustomPrompt = {
  id: string;
  name: string;
  body: string;
  created_at: string;
  updated_at: string;
};

export type CustomPromptsResponse = {
  prompts: CustomPrompt[];
  active_id: string | null;
};

export type UserDoc = {
  file_name: string;
  chunk_count: number;
  byte_size: number | null;
  uploaded_at: string | null;
  file_hash: string | null;
  file_type: string | null;
};

export type UserDocsResponse = {
  tenant_status: string;
  docs: UserDoc[];
};

export type UserDocUploadResult = {
  file_name: string;
  chunk_count: number;
  duplicate: boolean;
  error: string | null;
};

export type UserDocsUploadResponse = {
  results: UserDocUploadResult[];
  docs?: UserDoc[];
  tenant_status?: string;
};

export type UserDocFilterResponse = {
  file_names: string[] | null;
};

export type AdminUserSummary = {
  id: string;
  identifier: string;
  role: 'admin' | 'user';
  disabled: boolean;
  createdAt?: string | null;
  last_login_at?: string | null;
  provider?: string | null;
  total_tokens?: number;
  total_cost?: number;
  pricing_missing?: boolean;
  is_temporary?: boolean;
  temp_expires_at?: string | null;
};

export type TempLimits = {
  temporary: boolean;
  day_limit?: number | null;
  input_token_limit?: number | null;
  output_token_limit?: number | null;
  expire_in_minutes?: number | null;
};

export type TempDefaults = {
  days?: number | null;
  input_tokens?: number | null;
  output_tokens?: number | null;
};

export type TokenUsageTotals = {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
};

// A demo (temporary) account only ever receives a percentage remaining + an
// exhausted flag + expiry — never raw token limits or usage (#1054).
export type TempSelfView = {
  percent_remaining: number;
  exhausted: boolean;
  expires_at?: string | null;
};

// Demo users get only `temp`; everyone else gets `lifetime` + `session`.
export type UserTokenUsage = {
  lifetime?: TokenUsageTotals;
  session?: TokenUsageTotals;
  temp?: TempSelfView;
};

export type PerModelUsage = {
  model: string;
  provider: string;
  cost?: number;
  pricing_missing?: boolean;
} & TokenUsageTotals;

export type AdminUserTokenUsage = {
  lifetime: TokenUsageTotals;
  lifetime_cost?: number;
  pricing_missing?: boolean;
  window_days: number;
  per_day: ({ day: string } & TokenUsageTotals)[];
  per_model_30d: PerModelUsage[];
  per_model_lifetime: PerModelUsage[];
};

export type AdminUserDetail = AdminUserSummary & {
  last_login_ip?: string | null;
  email?: string | null;
  has_password: boolean;
  temp_day_limit?: number | null;
  temp_input_token_limit?: number | null;
  temp_output_token_limit?: number | null;
  temp_first_login_at?: string | null;
  tokens_used?: number;
  input_tokens_used?: number;
  output_tokens_used?: number;
};

export type SignupSubmission = {
  email: string;
  organization: string;
  intended_use: string;
  roe_acknowledged: boolean;
  provider?: 'credentials' | 'oauth';
};

export type SupportSubmission = {
  name?: string;
  email: string;
  phone?: string;
  organization?: string;
  subject: string;
  message: string;
};

export type PendingSignup = {
  id: string;
  email: string;
  organization: string | null;
  intended_use: string | null;
  roe_ack_at: string | null;
  provider: string;
  status: 'pending' | 'approved' | 'denied';
  deny_reason: string | null;
  decided_by: string | null;
  decided_at: string | null;
  requested_at: string;
};

export type AuditEvent = {
  id: number;
  ts: string | null;
  userId: string | null;
  userIdentifier: string | null;
  source: string;
  eventType: string;
  severity: string;
  operationId: string | null;
  threadId: string | null;
  payload: Record<string, unknown>;
  prevHash: string | null;
  eventHash: string;
  summary: string;
};

export type AuditEventPage = {
  events: AuditEvent[];
  next_before_id: number | null;
  latest_id: number | null;
};

export type AuditDbEvent = {
  id: number;
  ts: string | null;
  tableName: string;
  operation: string;
  rowPk: string | null;
  sessionUser: string;
  payload: Record<string, unknown>;
  rowHash: string;
};

export type AuditDbEventPage = {
  events: AuditDbEvent[];
  next_before_id: number | null;
  latest_id: number | null;
};

export type AuditFilters = {
  user_identifier?: string;
  user_id?: string;
  event_type?: string[];
  source?: string;
  severity?: string;
  ip?: string;
  operation_id?: string;
  thread_id?: string;
  since?: string;
  until?: string;
  before_id?: number;
  after_id?: number;
  limit?: number;
};

export type AuditSavedSearch = {
  id: string;
  name: string;
  filters: AuditFilters;
  created_at: string;
};

export type AuditDbFilters = {
  tableName?: string;
  operation?: string;
  since?: string;
  until?: string;
  before_id?: number;
  after_id?: number;
  limit?: number;
};

export type AuditVerifyStatus = {
  ok: boolean;
  rows_checked: number;
  error: string | null;
  checked_at: string;
};

export type AuditStats = {
  summary: {
    total: number;
    logins: number;
    failed_logins: number;
    active_users: number;
    exports: number;
  };
  by_event_type: { event_type: string; count: number }[];
  by_day: { day: string; count: number }[];
  top_users: { identifier: string; count: number }[];
  top_source_ips: { ip: string; count: number }[];
};

export class PasswordResetError extends Error {
  status: number;
  detail?: string;
  retryAfter?: number;

  constructor(
    message: string,
    status: number,
    detail?: string,
    retryAfter?: number
  ) {
    super(message);
    this.status = status;
    this.detail = detail;
    this.retryAfter = retryAfter;
  }
}

function buildAuditQueryString(filters: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        parts.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`
        );
      }
    } else {
      parts.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      );
    }
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

class ExtendedChainlitAPI extends ChainlitAPI {
  async shareThread(
    threadId: string,
    isShared: boolean
  ): Promise<{ success: boolean }> {
    const res = await this.put(`/project/thread/share`, {
      threadId,
      isShared
    });
    return res.json();
  }

  async listCustomPrompts(): Promise<CustomPromptsResponse> {
    const res = await this.get(`/user/custom-prompts`);
    return res.json();
  }

  async createCustomPrompt(name: string, body: string): Promise<CustomPrompt> {
    const res = await this.post(`/user/custom-prompts`, { name, body });
    return res.json();
  }

  async updateCustomPrompt(
    id: string,
    patch: { name?: string; body?: string }
  ): Promise<CustomPrompt> {
    const res = await this.put(`/user/custom-prompts/${id}`, patch);
    return res.json();
  }

  async deleteCustomPrompt(id: string): Promise<void> {
    await this.delete(`/user/custom-prompts/${id}`, {});
  }

  async setActiveCustomPrompt(
    id: string | null
  ): Promise<{ active_id: string | null }> {
    const res = await this.put(`/user/custom-prompts/active`, { id });
    return res.json();
  }

  async listUserDocs(): Promise<UserDocsResponse> {
    const res = await this.get(`/user/docs`);
    return res.json();
  }

  async uploadUserDocs(files: File[]): Promise<UserDocsUploadResponse> {
    const form = new FormData();
    files.forEach((file) => form.append('files', file, file.name));
    const base = this.httpEndpoint.endsWith('/')
      ? this.httpEndpoint
      : `${this.httpEndpoint}/`;
    const res = await fetch(new URL('user/docs', base), {
      method: 'POST',
      credentials: 'include',
      body: form
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data?.detail || 'Upload failed');
    }
    return data as UserDocsUploadResponse;
  }

  async deleteUserDoc(fileName: string): Promise<void> {
    await this.delete(`/user/docs/${encodeURIComponent(fileName)}`, {});
  }

  async getUserDocPreview(fileName: string): Promise<{ preview: string }> {
    const res = await this.get(
      `/user/docs/${encodeURIComponent(fileName)}/preview`
    );
    return res.json();
  }

  async getUserDocFilter(): Promise<UserDocFilterResponse> {
    const res = await this.get(`/user/docs/active-filter`);
    return res.json();
  }

  async setUserDocFilter(
    fileNames: string[] | null
  ): Promise<UserDocFilterResponse> {
    const res = await this.put(`/user/docs/active-filter`, {
      file_names: fileNames
    });
    return res.json();
  }

  async listAdminUsers(): Promise<{ users: AdminUserSummary[] }> {
    const res = await this.get(`/admin/users`);
    return res.json();
  }

  async setAdminUserRole(
    identifier: string,
    role: 'admin' | 'user'
  ): Promise<AdminUserSummary> {
    const res = await this.put(
      `/admin/users/${encodeURIComponent(identifier)}/role`,
      { role }
    );
    return res.json();
  }

  async setAdminUserDisabled(
    identifier: string,
    disabled: boolean
  ): Promise<AdminUserSummary> {
    const res = await this.put(
      `/admin/users/${encodeURIComponent(identifier)}/disabled`,
      { disabled }
    );
    return res.json();
  }

  async getAdminUser(identifier: string): Promise<AdminUserDetail> {
    const res = await this.get(
      `/admin/users/${encodeURIComponent(identifier)}`
    );
    return res.json();
  }

  async getSignupPolicy(): Promise<{ signups_enabled: boolean }> {
    const res = await this.get(`/admin/signup-policy`);
    return res.json();
  }

  async setSignupPolicy(
    enabled: boolean
  ): Promise<{ signups_enabled: boolean }> {
    const res = await this.put(`/admin/signup-policy`, { enabled });
    return res.json();
  }

  async getUserTokenUsage(): Promise<UserTokenUsage> {
    const res = await this.get(`/user/token-usage`);
    return res.json();
  }

  async getAdminUserTokenUsage(
    identifier: string
  ): Promise<AdminUserTokenUsage> {
    const res = await this.get(
      `/admin/users/${encodeURIComponent(identifier)}/token-usage`
    );
    return res.json();
  }

  async getThreadTokenUsage(threadId: string): Promise<TokenUsageTotals> {
    const res = await this.get(
      `/user/threads/${encodeURIComponent(threadId)}/token-usage`
    );
    return res.json();
  }

  async getThreadNotes(threadId: string): Promise<{ notes: string }> {
    const res = await this.get(
      `/user/threads/${encodeURIComponent(threadId)}/notes`
    );
    return res.json();
  }

  async saveThreadNotes(
    threadId: string,
    notes: string
  ): Promise<{ notes: string }> {
    const res = await this.put(
      `/user/threads/${encodeURIComponent(threadId)}/notes`,
      { notes }
    );
    return res.json();
  }

  async triggerAdminPasswordReset(
    identifier: string
  ): Promise<{ success: boolean; email: string }> {
    const res = await this.post(
      `/admin/users/${encodeURIComponent(identifier)}/password-reset`,
      {}
    );
    return res.json();
  }

  async submitSignup(
    payload: SignupSubmission
  ): Promise<{ ok: boolean; message: string; verification_email?: boolean }> {
    const res = await fetch(
      new URL(
        'signup',
        this.httpEndpoint.endsWith('/')
          ? this.httpEndpoint
          : `${this.httpEndpoint}/`
      ),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      }
    );
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(
        data.detail || 'Failed to submit signup'
      ) as Error & {
        status?: number;
      };
      err.status = res.status;
      throw err;
    }
    return data;
  }

  async submitSupportRequest(
    payload: SupportSubmission
  ): Promise<{ success: boolean }> {
    const res = await fetch(
      new URL(
        'support',
        this.httpEndpoint.endsWith('/')
          ? this.httpEndpoint
          : `${this.httpEndpoint}/`
      ),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      }
    );
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(
        data.detail || 'Failed to submit support request'
      ) as Error & {
        status?: number;
        retryAfter?: number;
      };
      err.status = res.status;
      if (res.status === 429 && typeof data.retry_after === 'number') {
        err.retryAfter = data.retry_after;
      }
      throw err;
    }
    return data;
  }

  async listPendingSignups(): Promise<{
    signups: PendingSignup[];
    temp_defaults?: TempDefaults;
  }> {
    const res = await this.get(`/admin/pending-signups`);
    return res.json();
  }

  async approvePendingSignup(
    signupId: string,
    temp?: TempLimits
  ): Promise<PendingSignup> {
    const res = await this.post(
      `/admin/pending-signups/${encodeURIComponent(signupId)}/approve`,
      temp ?? {}
    );
    return res.json();
  }

  async setAdminUserTemp(
    identifier: string,
    temp: TempLimits
  ): Promise<AdminUserDetail> {
    const res = await this.put(
      `/admin/users/${encodeURIComponent(identifier)}/temp`,
      temp
    );
    return res.json();
  }

  async createAdminUser(body: {
    username: string;
    password: string;
    email?: string | null;
    role: 'admin' | 'user';
    temporary: boolean;
    day_limit?: number | null;
    input_token_limit?: number | null;
    output_token_limit?: number | null;
    expire_in_minutes?: number | null;
  }): Promise<AdminUserDetail> {
    const res = await this.post(`/admin/users`, body);
    return res.json();
  }

  async denyPendingSignup(
    signupId: string,
    denyReason: string
  ): Promise<PendingSignup> {
    const res = await this.post(
      `/admin/pending-signups/${encodeURIComponent(signupId)}/deny`,
      { deny_reason: denyReason }
    );
    return res.json();
  }

  async deleteAdminUser(identifier: string): Promise<void> {
    await this.delete(`/admin/users/${encodeURIComponent(identifier)}`, {});
  }

  async purgeAdminUserData(identifier: string): Promise<{ threads_deleted: number; purged: boolean }> {
    const res = await this.delete(`/admin/users/${encodeURIComponent(identifier)}/data`, {});
    return res.json();
  }

  async listAuditEvents(filters: AuditFilters = {}): Promise<AuditEventPage> {
    const params = buildAuditQueryString(filters);
    const res = await this.get(`/admin/audit-events${params}`);
    return res.json();
  }

  async listAuditDbEvents(
    filters: AuditDbFilters = {}
  ): Promise<AuditDbEventPage> {
    const params = buildAuditQueryString(filters);
    const res = await this.get(`/admin/audit-db-events${params}`);
    return res.json();
  }

  async exportAuditEvents(
    filters: AuditFilters,
    format: 'jsonl' | 'csv' | 'json' | 'xlsx' | 'pdf'
  ): Promise<Blob> {
    const params = buildAuditQueryString({ ...filters, format });
    const base = this.httpEndpoint.endsWith('/')
      ? this.httpEndpoint
      : `${this.httpEndpoint}/`;
    const res = await fetch(
      new URL(`admin/audit-events/export${params}`, base),
      {
        method: 'GET',
        credentials: 'include'
      }
    );
    if (!res.ok) {
      throw new Error(`Audit export failed: ${res.status}`);
    }
    return res.blob();
  }

  async verifyAuditChain(): Promise<AuditVerifyStatus> {
    const res = await this.get(`/admin/audit-events/verify`);
    return res.json();
  }

  async listAuditSavedSearches(): Promise<{ searches: AuditSavedSearch[] }> {
    const res = await this.get(`/admin/audit-events/saved-searches`);
    return res.json();
  }

  async createAuditSavedSearch(name: string, filters: AuditFilters): Promise<AuditSavedSearch> {
    const res = await this.post(`/admin/audit-events/saved-searches`, { name, filters });
    return res.json();
  }

  async deleteAuditSavedSearch(id: string): Promise<void> {
    await this.delete(`/admin/audit-events/saved-searches/${encodeURIComponent(id)}`, {});
  }

  async getAuditStats(since?: string, until?: string): Promise<AuditStats> {
    const params = buildAuditQueryString({ since, until });
    const res = await this.get(`/admin/audit-events/stats${params}`);
    return res.json();
  }

  async forgotPassword(email: string): Promise<void> {
    const base = this.httpEndpoint.endsWith('/')
      ? this.httpEndpoint
      : `${this.httpEndpoint}/`;
    await fetch(new URL('forgot-password', base), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
  }

  async validateResetToken(
    token: string,
    signal?: AbortSignal
  ): Promise<{ valid: boolean }> {
    const base = this.httpEndpoint.endsWith('/')
      ? this.httpEndpoint
      : `${this.httpEndpoint}/`;
    const res = await fetch(new URL('reset-password/check', base), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
      signal
    });
    if (res.ok) {
      try {
        const body = await res.json();
        return { valid: body?.valid === true };
      } catch {
        return { valid: false };
      }
    }
    let detail: string | undefined;
    let retryAfter: number | undefined;
    try {
      const body = await res.json();
      detail = typeof body?.detail === 'string' ? body.detail : undefined;
      if (typeof body?.retry_after === 'number') {
        retryAfter = body.retry_after;
      }
    } catch {
      // body may not be JSON
    }
    if (retryAfter === undefined) {
      const header = res.headers.get('Retry-After');
      if (header) {
        const parsed = Number.parseInt(header, 10);
        if (!Number.isNaN(parsed)) retryAfter = parsed;
      }
    }
    throw new PasswordResetError(
      res.statusText || 'Token check failed',
      res.status,
      detail,
      retryAfter
    );
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    const base = this.httpEndpoint.endsWith('/')
      ? this.httpEndpoint
      : `${this.httpEndpoint}/`;
    const res = await fetch(new URL('reset-password', base), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword })
    });
    if (res.ok) return;

    let detail: string | undefined;
    let retryAfter: number | undefined;
    try {
      const body = await res.json();
      detail = typeof body?.detail === 'string' ? body.detail : undefined;
      if (typeof body?.retry_after === 'number') {
        retryAfter = body.retry_after;
      }
    } catch {
      // body may not be JSON
    }
    if (retryAfter === undefined) {
      const header = res.headers.get('Retry-After');
      if (header) {
        const parsed = Number.parseInt(header, 10);
        if (!Number.isNaN(parsed)) retryAfter = parsed;
      }
    }
    throw new PasswordResetError(
      res.statusText || 'Reset failed',
      res.status,
      detail,
      retryAfter
    );
  }

  connectStreamableHttpMCP(
    sessionId: string,
    name: string,
    url: string,
    headers?: Record<string, string>
  ) {
    // Assumes the backend expects { clientType, name, url }
    return fetch(
      new URL(
        'mcp',
        this.httpEndpoint.endsWith('/')
          ? this.httpEndpoint
          : `${this.httpEndpoint}/`
      ),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'x-session-id': sessionId } : {})
        },
        body: JSON.stringify({
          clientType: 'streamable-http',
          name,
          url,
          sessionId,
          ...(headers ? { headers } : {})
        })
      }
    ).then(async (res) => {
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to connect MCP');
      }
      return { success: true, mcp: data.mcp };
    });
  }

  async getLegalStatus(): Promise<LegalStatus> {
    const res = await this.get(`/user/legal/status`);
    return res.json();
  }

  async acceptLegal(checkboxes: LegalCheckboxes): Promise<LegalAcceptResponse> {
    const res = await this.post(`/user/legal/accept`, { checkboxes });
    return res.json();
  }

  async resetUserLegal(
    identifier: string
  ): Promise<{ deleted: boolean; identifier: string }> {
    const res = await this.post(
      `/admin/users/${encodeURIComponent(identifier)}/legal-reset`,
      {}
    );
    return res.json();
  }

  async getLegalDisclaimerText(): Promise<LegalText> {
    const res = await this.get(`/user/legal/disclaimer-text`);
    return res.json();
  }

  async getLegalTerminalText(): Promise<LegalText> {
    const res = await this.get(`/user/legal/terminal-text`);
    return res.json();
  }

  async getPrivacyStatus(): Promise<PrivacyStatus> {
    const res = await this.get(`/user/privacy/status`);
    return res.json();
  }

  async acceptPrivacy(
    checkboxes: PrivacyCheckboxes
  ): Promise<PrivacyAcceptResponse> {
    const res = await this.post(`/user/privacy/accept`, { checkboxes });
    return res.json();
  }

  async resetUserPrivacy(
    identifier: string
  ): Promise<{ deleted: boolean; identifier: string }> {
    const res = await this.post(
      `/admin/users/${encodeURIComponent(identifier)}/privacy-reset`,
      {}
    );
    return res.json();
  }

  async getPrivacyText(): Promise<LegalText> {
    const res = await this.get(`/user/privacy/text`);
    return res.json();
  }
}

export type LegalStatus = {
  accepted: boolean;
  needs_reprompt: boolean;
  disclaimer_version: string | null;
  disclaimer_version_hash: string | null;
  current_disclaimer_hash: string;
  current_terminal_hash: string;
};

export type LegalCheckboxes = {
  authorized_user: boolean;
  written_authorization: boolean;
  terminal_responsibility: boolean;
};

export type LegalAcceptResponse = {
  accepted: boolean;
  disclaimer_version: string | null;
  disclaimer_version_hash: string | null;
  terminal_version_hash: string | null;
};

export type LegalText = {
  version: string;
  version_hash: string;
  text: string;
};

export type PrivacyStatus = {
  accepted: boolean;
  needs_reprompt: boolean;
  privacy_version: string | null;
  privacy_version_hash: string | null;
  current_privacy_hash: string;
};

export type PrivacyCheckboxes = {
  privacy_policy: boolean;
};

export type PrivacyAcceptResponse = {
  accepted: boolean;
  privacy_version: string | null;
  privacy_version_hash: string | null;
};

export const apiClient = new ExtendedChainlitAPI(
  httpEndpoint,
  'webapp',
  {}, // Optional - additionalQueryParams property.
  on401,
  onError
);
